/**
 * Table Column Configuration Types
 * 
 * @typedef {Object} ColumnConfig
 * @property {string} key - Unique key to access data from row object
 * @property {string} headerName - Display name for column header
 * @property {boolean} [sortable=true] - Whether column is sortable
 * @property {('string'|'number'|'date'|'currency'|'percentage')} [dataType='string'] - Data type for sorting
 * @property {string} [dateFormat] - Expected date format (e.g., 'MM/DD/YYYY', 'YYYY-MM-DD')
 * @property {Function} [render] - Custom render function (value, row) => JSX
 * @property {string} [width] - Column width (e.g., '150px', '20%')
 * @property {string} [align='left'] - Text alignment ('left', 'center', 'right')
 * @property {string} [className] - Additional CSS class for column
 */

/**
 * Sort Configuration
 * 
 * @typedef {Object} SortConfig
 * @property {string} key - Column key to sort by
 * @property {('asc'|'desc')} direction - Sort direction
 */

/**
 * Pagination Configuration
 * 
 * @typedef {Object} PaginationConfig
 * @property {number} [pageSize=10] - Rows per page
 * @property {number[]} [pageSizeOptions=[10, 25, 50, 100]] - Available page size options
 * @property {boolean} [showPageSizeSelector=true] - Show page size dropdown
 * @property {boolean} [showPageInfo=true] - Show "Page X of Y" info
 */

/**
 * Table Props
 * 
 * @typedef {Object} TableProps
 * @property {Array<Object>} data - Array of row data objects
 * @property {Array<ColumnConfig>} columns - Column configuration array
 * @property {SortConfig} [defaultSort] - Initial sort configuration
 * @property {PaginationConfig} [pagination] - Pagination configuration
 * @property {boolean} [enablePagination=true] - Enable/disable pagination
 * @property {string} [emptyMessage='No data available'] - Message when no data
 * @property {boolean} [loading=false] - Show loading state
 * @property {string} [className] - Additional CSS class for table container
 * @property {boolean} [striped=true] - Alternate row colors
 * @property {boolean} [hoverable=true] - Highlight row on hover
 * @property {Function} [onRowClick] - Callback when row is clicked (row, index) => void
 * @property {string} [tableId] - Unique ID for the table
 */

// Data type constants
export const DATA_TYPES = {
  STRING: 'string',
  NUMBER: 'number',
  DATE: 'date',
  CURRENCY: 'currency',
  PERCENTAGE: 'percentage'
};

// Sort direction constants
export const SORT_DIRECTION = {
  ASC: 'asc',
  DESC: 'desc'
};

// Default configurations
export const DEFAULT_PAGE_SIZE = 10;
export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
